import { Redirect } from "react-router";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { usePermission } from "helpers/globalContext/usePermission";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";

import { routes } from "utilities/routes";

import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";

import { type ISelectionListContainerHandler } from "entity/selection/SelectionListContainer/SelectionListContainer.api.handler";
import { useSelectionConfig } from "entity/selection/SelectionListContainer/useSelectionConfig";
import { TemplateListTabs } from "entity/template/TemplateList/TemplateListTab/TemplateListTab.api.types";

interface ISelectionsAreaRouteGuardProps {
    /** Currently selected job/template, or null when there's no valid selection. */
    jobId: number | null;
    /** Whether the app is in template mode; the redirect only applies to templates. */
    isTemplateMode: boolean;
    /** The legacy Selections area to render when the selection is a legacy (non-SRI) job. */
    children: React.ReactNode;
    /** Injectable for testing; defaults to the real config handler. */
    handler?: ISelectionListContainerHandler;
}

/**
 * Keeps the legacy Selections area unreachable for SRI templates, which have no legacy Selections
 * experience — a bookmark or stale link lands here with an SRI template selected and is sent to My
 * Templates instead of rendering a page that doesn't apply.
 *
 * Regular jobs are never redirected: an SRI job has its own Selections experience to route to, and
 * My Templates would be a nonsensical destination while not in template mode. Builders without the
 * SRI flag are never redirected either, and never pay for the config fetch.
 *
 * Template Support for Selection Options makes the area reachable on any template, so with that flag
 * on no template redirects — matching the main-nav item, which the same flag makes visible there.
 */
export const SelectionsAreaRouteGuard = ({
    jobId,
    isTemplateMode,
    children,
    handler,
}: ISelectionsAreaRouteGuardProps) => {
    const builderInfo = useRequiredContext(BuilderInfoContext);
    // The config endpoint requires this same permission, so without it the fetch could only 403
    const canViewSelections = usePermission("selections", "canView");

    // Only a template on an SRI builder without template selection-options support can redirect;
    // anything else can't, so skip the fetch
    const canRedirect =
        isTemplateMode &&
        builderInfo.flags.selectionsReimagined &&
        !builderInfo.flags.templateSupportForSelectionOptions &&
        canViewSelections;
    const { config, isLoading } = useSelectionConfig(canRedirect ? jobId : null, handler);

    if (isLoading) {
        return <BTLoading data-testid="BTLoading" />;
    }

    if (canRedirect && config?.isSelectionsReimaginedJob) {
        return <Redirect to={routes.template.getListLink(TemplateListTabs.MyTemplates)} />;
    }

    // No config (legacy job or a load error the hook already surfaced) → legacy area as safe default
    return <>{children}</>;
};
